import { useEffect, useState } from "react";
import {
  getTrackingEvent,
  getCustomVoucher,
  postTrackingBundle,
} from "../../../../services/offers/offers.service";
import {
  CampaignType,
  MediaType,
  Offer,
  OfferResolutionMedia,
  OtherSettingsInterface,
  PlacementStage,
  QueryBundle,
  RedeemBy,
  Template,
  TemplateOptionSettings,
  TextAlign,
  VoucherType,
  LayoutOptions,
  ICONS_DEFAULT_VALUES,
} from "../../../../types/global.types";
import offerImageSrc from "./../../../../assets/images/offer-listening.png";
import PreSelect from "../../components/PreSelect/PreSelect";
import TemplateIcon from "../../components/Icons/info.component";
import BlockedTag from "../../components/BlockedTag/blockedTag.component";
import ImageCarousel from "../../../ImageCarousel/ImageCarousel.component";
interface Props {
  offer: Offer;
  slot: number;
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  displayOnly: boolean;
  isAdded: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaign: CampaignType;
  emailSentIds: string[];
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  hasVoucher: boolean;
  otherSettings: OtherSettingsInterface | undefined;
  sessionId: string;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
  canAutoSendEmailFromLabel?: boolean;
  canAutoSendEmailFromMainCTA?: boolean;
  onAutoSendEmailFromLabel?: (offer: Offer, slot: number) => void;
  autoSendingOfferId?: boolean;
}

const OfferItem = ({
  offer,
  slot,
  settings,
  emailSentIds,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  isAdded,
  openSingle,
  onAdd,
  hasVoucher,
  otherSettings,
  sessionId,
  layoutId,
  layoutOptions,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const [copyCode, setCopyCode] = useState<boolean>(false);
  const [getCode, setGetCode] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [customVoucher, setCustomVoucher] = useState<string>("");
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);
  const [platform, setPlatform] = useState("");

  const handleAdd = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLElement>
      | React.MouseEvent<HTMLElement>,
    value: boolean
  ) => {
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value, slot);
      value && handleOpenEmailInput();
    }
  };

  const handleOpenEmailInput = () => {
    if (
      campaign.campaignGeneralOptions.showAddToOrder &&
      campaign.campaignGeneralOptions.emailCaptureOnOffers &&
      campaign.campaignGeneralOptions.emailCaptureOnAddToOrder
    ) {
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = async (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    url: string
  ) => {
    if (offer?.voucher?.voucherType === VoucherType.CUSTOM && !getCode) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("item-info-icon") &&
      !event.target.classList.contains("item-icon-wrap") &&
      !event.target.classList.contains("item-email-icon") &&
      !event.target.classList.contains("copy-box") &&
      !displayOnly &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      if (offer.offerSettings.bundleOptions.isBundled) {
        const data: QueryBundle = {
          offerId: offer.id,
          campaignId: campaign.id,
          placementId: placementId,
          orderId: offer.orderId,
          userSessionId: sessionId,
          imageUrl: getImageUrl(offer),
          pageUrl: window.location.href,
          templateId: settingsTemplate.identifier,
          placementName: placementName,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          slot: slot,
          layoutId: layoutId,
        };

        const response = await postTrackingBundle(data);

        if (response?.data?.status) {
          window.open(`/bundle-offer/${response?.data?.result}`, "_blank");
        }
      } else {
        window.open(
          url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
          "_blank"
        );
      }
    }
  };

  const handleClickInfo = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    openSingle(offer, false, false, slot, true);
  };

  const handleMainCtaAutoSend = () => {
    if (
      canAutoSendEmailFromMainCTA &&
      !!onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled &&
      !autoSendingOfferId
    ) {
      onAutoSendEmailFromLabel(offer, slot);
    }
  };

  const handleSaveEmail = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      canAutoSendEmailFromLabel &&
      !!onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      onAutoSendEmailFromLabel(offer, slot);
      return;
    }

    openSingle(offer, true, false, slot);
  };

  const handleCopyHtml = () => {
    window.focus();
    navigator.clipboard?.writeText(
      customVoucher ? customVoucher : offer.voucher?.code
    );
    setCopied(true);
    setCopyCode(false);
    setTimeout(() => {
      setCopied(false);
    }, 1000); // Show the message for 1 second
  };

  const handleGetCode = async () => {
    setVoucherLoading(true);
    const customVoucher = await getCustomVoucher({
      offerId: offer.id,
      campaignId: campaign.id,
      campaignName: campaign.name,
      companyId: campaign.company_Id,
      placementId: placementId,
      placementName: placementName,
      templateId: settingsTemplate.identifier,
      layoutId: layoutId,
      orderId: offer.orderId,
    });
    setCustomVoucher(customVoucher?.data?.result);
    setGetCode(true);
    setVoucherLoading(false);
  };

  const getMediaResolutionByType = (
    type: MediaType,
    offer: Offer
  ): OfferResolutionMedia | undefined => {
    let media: OfferResolutionMedia | undefined = offer.media.resolution.find(
      (r) => r.type === type
    );

    return media;
  };

  const getImageUrl = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);
    return media && media.images.length
      ? media.images[0].filepath
      : offer.imageUrl || offerImageSrc;
  };

  const getTitle = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);
    return media?.title || offer.media.featured.title || offer.title;
  };

  const getDescription = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);

    return (
      media?.description ||
      offer.media.featured.description ||
      offer.description
    );
  };

  const getImages = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);
    return media && media.images.length
      ? media.images.map((image) => image.filepath)
      : offer.media.mainImage.length > 0
        ? offer.media.mainImage.map((image) => image.filepath)
        : [offer.imageUrl || offerImageSrc];
  };

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    // Check if the platform is iOS
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform("ios");
    }
    // Check if the platform is macOS
    else if (/macintosh|macintel|macppc|mac68k|macos/.test(userAgent)) {
      setPlatform("mac");
    } else {
      setPlatform("other");
    }
  }, []);

  const getVoucherText = () => {
    return customVoucher ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>

        <span style={{ fontWeight: "600" }}>{customVoucher}</span>
      </>
    ) : offer.voucher?.code ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>
        <span style={{ fontWeight: "600" }}>{offer.voucher?.code}</span>
      </>
    ) : (
      <span style={{ fontWeight: "400" }}>Sorry, code just expired!</span>
    );
  };

  return (
    <>
      <div
        className={`swiper-item ${displayOnly && campaign.campaignGeneralOptions.showAddToOrder
          ? "display"
          : "getbutton"
          }`}
        onClick={(e) => handleGetGift(e, offer.trackingUrl)}
      >
        <input
          className="input"
          id={offer.id}
          type="checkbox"
          disabled={
            !displayOnly || !campaign.campaignGeneralOptions.showAddToOrder
          }
          defaultChecked={offer.isAdded || false}
          onChange={(e) => handleAdd(e, !offer.isAdded || false)}
        />
        {(layoutOptions?.showBadge ?? true) && (
          <span
            style={{
              position: "absolute",
              top: "-8.5px",
              left: "10px",
              fontSize: "10px",
              textTransform: "uppercase",
              color: offer.badge?.fontColor,
              backgroundColor: offer.badge?.backgroundColor,
              padding: "0 6px",
              borderRadius: "3px",
              fontWeight: "600",
              zIndex: 1,
            }}
            className="badge-settings"
          >
            {offer.badge?.name}
          </span>
        )}
        <label
          htmlFor={offer.id}
          className="item-layout-eh item-layout--embedded-horizontal"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              displayOnly
                ? handleAdd(e, !offer.isAdded || false)
                : handleGetGift(e, offer.trackingUrl);
            }
          }}
          style={{
            backgroundColor: settings?.dealLayout.backgroundColor,
            fontSize: settings?.dealLayout.fontSize,
            color: settings?.dealLayout.textColor,
            border: offer.isAdded
              ? `1px solid ${settings.mainLayout?.highlightColor}`
              : offer.badge?.useBorder && (layoutOptions?.showBadge ?? true)
                ? `1px solid ${offer.badge?.backgroundColor}`
                : settings.dealLayout?.borderColor
                  ? `1px solid ${settings.dealLayout?.borderColor}`
                  : "#ddd",
            borderRadius: `${settings.mainLayout?.borderRadius}px`,
            position: "relative",
          }}
        >
          {offer.tagBlocked.isBlocked && (
            <BlockedTag tagBlocked={offer.tagBlocked} />
          )}

          <div
            className="item-image"
            style={{
              borderRadius: `${settings?.mainLayout?.borderRadius}px`,
            }}
          >
            <div className="offer-preview-swiper">
              <ImageCarousel
                images={getImages(offer)}
                showPagination={true}
                borderRadius={settings?.mainLayout?.borderRadius}
                altPrefix="offer"
                style={{ width: "100%", height: "auto" }}
              />
            </div>
          </div>

          <div
            className="item-content-wrapper"
            style={{
              textAlign: settings?.dealLayout.align || TextAlign.center,
              paddingBottom: hasVoucher ? "20px" : "0",
            }}
          >
            <div className={`item-content`}>
              <div className="item-title">
                <span
                  className={`item-title-text max-2lines `}
                  style={{
                    color: settings?.dealLayout.textColor,
                    fontSize: `${Number(settings?.dealLayout.fontSize) + 1}px`,
                  }}
                  tabIndex={0}
                  role="contentinfo"
                  aria-label={getTitle(offer)}
                >
                  {getTitle(offer)}
                </span>
              </div>
              <div
                className="item-description max-2lines"
                style={{
                  color: settings?.dealLayout.textColor,
                  fontSize: `${settings?.dealLayout.fontSize}px`,
                }}
                tabIndex={0}
                role="contentinfo"
                aria-label={getDescription(offer)}
              >
                {getDescription(offer)}
              </div>

              {offer.offerSettings.redeemBy === RedeemBy.email && (
                <>
                  {emailSentIds.includes(offer.id) && (
                    <>
                      {offer.offerSettings.delaySetting.delayValue === "0" ||
                        offer.offerSettings.delaySetting.delayType === "none" ? (
                        <span className="item-delay-email">
                          Thank you! We have emailed your offer!
                        </span>
                      ) : (
                        <span className="item-delay-email">
                          Thank you! You will receive the offer in{" "}
                          {offer.offerSettings.delaySetting.delayValue}{" "}
                          {offer.offerSettings.delaySetting.delayType}!
                        </span>
                      )}
                    </>
                  )}
                </>
              )}

              <div className="item-get">
                <div
                  className="item-get-center"
                  style={{
                    justifyContent:
                      settings?.dealLayout.align === TextAlign.right
                        ? "flex-end"
                        : settings?.dealLayout.align === TextAlign.left
                          ? "flex-start"
                          : "center",
                  }}
                >
                  {displayOnly ? (
                    <>
                      {campaign.campaignGeneralOptions.showAddToOrder && (
                        <PreSelect
                          offerId={offer.id}
                          buttonFontWeight={
                            settings?.dealLayout.buttonFontWeight
                          }
                          buttonFontSize={settings?.dealLayout.buttonFontSize}
                          showCheckboxSelect={otherSettings?.showCheckboxSelect}
                          checked={isAdded}
                          highlightColor={settings.mainLayout?.highlightColor}
                          selectedButtonText={otherSettings?.selectedButtonText}
                          notSelectedButtonText={
                            otherSettings?.notSelectedButtonText
                          }
                          textColor={otherSettings?.textColor}
                          settings={otherSettings}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {offer?.voucher?.voucherType === VoucherType.CUSTOM &&
                        !getCode ? (
                        <div
                          className="item-nlh-dashed-button-es copy-box"
                          onClick={handleGetCode}
                          tabIndex={0}
                          role="button"
                          aria-label="Get code"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleGetCode();
                            }
                          }}
                          style={{
                            fontSize: `${settings?.dealLayout.buttonFontSize}px`,
                            position: "relative",
                          }}
                        >
                          {voucherLoading ? (
                            <svg
                              className="spin"
                              viewBox="0 0 1024 1024"
                              focusable="false"
                              data-icon="loading"
                              width="1em"
                              height="1em"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                            </svg>
                          ) : (
                            "Get code"
                          )}
                        </div>
                      ) : (
                        offer.offerSettings.redeemBy === RedeemBy.webemail && (
                          <div
                            className="item-action-get max-1lines"
                            tabIndex={0}
                            role="button"
                            aria-label="Get offer now"
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleGetGift(e, offer.trackingUrl);
                              handleMainCtaAutoSend();
                            }}
                            onKeyDown={(
                              e: React.KeyboardEvent<HTMLElement>
                            ) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMainCtaAutoSend();
                                handleGetGift(e, offer.trackingUrl);
                              }
                            }}
                            style={{
                              color: settings?.dealLayout.buttonTextColor,
                              fontSize: `${settings?.dealLayout.buttonFontSize}px`,
                              fontWeight: settings?.dealLayout.buttonFontWeight,
                              backgroundColor:
                                settings?.dealLayout.buttonBackgroundColor,
                              borderRadius: settings?.dealLayout.buttonRounding,
                              border: `1px solid ${settings?.dealLayout.buttonBorderColor ||
                                settings?.dealLayout.buttonBackgroundColor
                                }`,
                              textTransform: settings?.dealLayout.buttonTextTransform,
                            }}
                          >
                            {offer?.offerCTAText || "Get offer now"}
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="item-content-icons">
              <div
                className="item-info-icon mbot5"
                onClick={handleClickInfo}
                tabIndex={0}
                role="button"
                aria-label="More information about offer"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleClickInfo(e);
                  }
                }}
                style={{
                  borderColor: settings?.dealLayout.borderColor || "#ddd",
                }}
              >
                <TemplateIcon
                  icon={
                    settings?.dealLayout?.iconInfo?.value ||
                    ICONS_DEFAULT_VALUES.iconInfo
                  }
                  color={settings?.dealLayout.textColor || "#888888"}
                />
              </div>

              {!displayOnly &&
                campaign.sharingOptions?.allowSharing &&
                campaign.sharingOptions.sharingPlatforms?.platforms?.length >
                0 &&
                offer?.offerSettings?.allowShare &&
                !offer.offerSettings?.bundleOptions?.isBundled &&
                (layoutOptions?.showShare ?? true) && (
                  <div
                    className="item-icon-wrap"
                    onClick={(e) => handleClickShare(e)}
                    tabIndex={0}
                    role="button"
                    aria-label="Share offer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleClickShare(e);
                      }
                    }}
                  >
                    <div
                      className="item-share-icon item-info-icon mbot5"
                      style={{
                        borderColor: settings?.dealLayout.borderColor || "#ddd",
                      }}
                    >
                      {platform === "ios" || platform === "mac" ? (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 3L8 6.85714M12 3L16 6.85714M12 3V16"
                            stroke={settings?.dealLayout.textColor}
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14.5 10H16C17.6569 10 19 11.3431 19 13V19C19 20.6569 17.6569 22 16 22H8C6.34315 22 5 20.6569 5 19V13C5 11.3431 6.34315 10 8 10H9.5"
                            stroke={settings?.dealLayout.textColor}
                            strokeWidth="1.2"
                          />
                        </svg>
                      ) : (
                        <TemplateIcon
                          icon={
                            settings?.dealLayout?.iconShare?.value ||
                            ICONS_DEFAULT_VALUES.iconShare
                          }
                          color={settings?.dealLayout.textColor || "#888888"}
                        />
                      )}
                    </div>
                  </div>
                )}

              {!displayOnly &&
                campaign.campaignGeneralOptions.saveOfferOptions
                  .allowSavingOffers &&
                campaign.campaignGeneralOptions.saveOfferOptions.allowEmail &&
                !offer.offerSettings?.bundleOptions?.isBundled &&
                (layoutOptions?.showEmail ?? true) && (
                  <div
                    className="item-icon-wrap"
                    onClick={handleSaveEmail}
                    tabIndex={0}
                    role="button"
                    aria-label={
                      autoSendingOfferId
                        ? "Sending email"
                        : "Save offer to email"
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleSaveEmail(e);
                      }
                    }}
                  >
                    <span
                      className="item-email-icon mbot5"
                      style={{
                        borderColor: settings?.dealLayout.borderColor || "#ddd",
                      }}
                    >
                      <TemplateIcon
                        icon={
                          settings?.dealLayout?.iconEmail?.value ||
                          ICONS_DEFAULT_VALUES.iconEmail
                        }
                        color={settings?.dealLayout.textColor || "#888888"}
                      />
                    </span>
                  </div>
                )}
            </div>
          </div>
          {offer.voucher?.voucherType && (
            <span
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                fontSize: "11px",
                color: settings?.dealLayout.buttonTextColor,
                backgroundColor: settings?.dealLayout.buttonBackgroundColor,
                borderRadius:
                  typeof settings?.mainLayout.borderRadius === "number"
                    ? `0 0 ${settings?.mainLayout.borderRadius - 1}px ${settings?.mainLayout.borderRadius - 1
                    }px`
                    : "0 0 7px 7px",
                fontWeight: "600",
                textAlign: "center",
                width: "100%",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onMouseEnter={() => setCopyCode(true)}
              onMouseLeave={() => {
                setCopyCode(false);
              }}
              onClick={() => handleCopyHtml()}
              tabIndex={0}
              role="button"
              aria-label="Copy code"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  handleCopyHtml();
                }
              }}
            >
              {offer.voucher?.voucherType === VoucherType.CUSTOM && !getCode ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                  className="copy-box"
                >
                  Code:
                  <svg
                    style={{
                      marginLeft: "5px",
                    }}
                    width="70"
                    height="16"
                    viewBox="0 0 70 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="70" height="16" fill="" />
                    <circle
                      cx="4"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="10"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="52"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="58"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="16"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="22"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="28"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="34"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="40"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                    <circle
                      cx="46"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout.buttonTextColor || "white"}
                    />
                  </svg>
                </div>
              ) : (
                <>
                  {copyCode ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      onClick={handleCopyHtml}
                      role="button"
                      aria-label="Copy code"
                      className="copy-box"
                    >
                      {copied ? (
                        <>
                          <svg
                            width="8"
                            height="6"
                            viewBox="0 0 8 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M1 3L3 5L7 1"
                              stroke={
                                settings?.dealLayout.buttonTextColor || "white"
                              }
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copied successfully
                        </>
                      ) : (
                        <>
                          <svg
                            width="11"
                            height="10"
                            viewBox="0 0 11 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M7.75 3V2C7.75 1.73478 7.63147 1.48043 7.42049 1.29289C7.20952 1.10536 6.92337 1 6.625 1H2.125C1.82663 1 1.54048 1.10536 1.3295 1.29289C1.11853 1.48043 1 1.73478 1 2V6C1 6.26522 1.11853 6.51957 1.3295 6.70711C1.54048 6.89464 1.82663 7 2.125 7H3.25M4.375 3H8.875C9.49632 3 10 3.44772 10 4V8C10 8.55228 9.49632 9 8.875 9H4.375C3.75368 9 3.25 8.55228 3.25 8V4C3.25 3.44772 3.75368 3 4.375 3Z"
                              stroke={
                                settings?.dealLayout.buttonTextColor || "white"
                              }
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copy Code
                        </>
                      )}
                    </div>
                  ) : copied ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      className="copy-box"
                    >
                      <svg
                        width="8"
                        height="6"
                        viewBox="0 0 8 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          marginRight: "5px",
                        }}
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke={
                            settings?.dealLayout.buttonTextColor || "white"
                          }
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied successfully
                    </div>
                  ) : (
                    getVoucherText()
                  )}
                </>
              )}
            </span>
          )}
        </label>
      </div>
    </>
  );
};

export default OfferItem;
